import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  Divider,
  IconButton,
  Fade,
  Link,
  Grid
} from '@mui/material';
import {
  Lock,
  Email,
  ArrowBack,
  Visibility,
  VisibilityOff,
  Business,
  TrendingUp,
  Security,
  Analytics
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { LoginFormData } from '../types';
import { api } from '@services';
import { API_CONFIG } from '../../../config/api';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useTheme();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isLoginComplete, setIsLoginComplete] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiInfo, setApiInfo] = useState<{url?: string, status?: string}>({});

  // Check API connection on component mount for local development
  useEffect(() => {
    const checkApiConnection = async () => {
      try {
        const baseUrl = API_CONFIG.BASE_URL;
        setApiInfo(prev => ({ ...prev, url: baseUrl }));

        if ((process.env as any).NODE_ENV === 'development') {
          setApiInfo(prev => ({ ...prev, status: 'checking...' }));
          try {
            await fetch(baseUrl, {
              method: 'HEAD',
              mode: 'no-cors',
            });
            setApiInfo(prev => ({ ...prev, status: 'connected' }));
          } catch (error) {
            console.warn('API connection test failed:', error);
            setApiInfo(prev => ({ ...prev, status: 'unreachable' }));
          }
        }
      } catch (error) {
        console.error('Error checking API connection:', error);
      }
    };

    checkApiConnection();
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    const formData: LoginFormData = {
      email: event.currentTarget.email.value,
      password: event.currentTarget.password.value,
    };

    try {
      console.log('Attempting login...');
      if ((process.env as any).NODE_ENV === 'development') {
        console.log('Using API:', API_CONFIG.BASE_URL);
      }

      const response = await api.post('/auth/login', formData);
      console.log('Login response received:', {
        status: response.status,
        hasToken: !!response.data?.token,
        hasUser: !!response.data?.user
      });

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);

        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        setIsLoginComplete(true);

        try {
          await login(formData.email, formData.password);
          console.log('Auth context updated');
          navigate('/dashboard', { replace: true });
        } catch (loginError) {
          console.error('Login context error:', loginError);
          navigate('/dashboard', { replace: true });
        }
      } else {
        throw new Error('No token received from server');
      }
    } catch (error: any) {
      console.error('Login error:', error.response || error);

      if (error.message === 'Network Error') {
        setError('Unable to connect to the server. Please check your network connection and make sure the backend is running.');
      } else if (error.response?.status === 429) {
        setError('Too many login attempts. Please wait a few minutes before trying again.');
      } else if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Login failed. Please check your credentials.');
      }

      setIsLoginComplete(false);
    } finally {
      setLoading(false);
    }
  };

  const clearAuthState = () => {
    localStorage.removeItem('token');
    setError('Auth state cleared. Try logging in again.');
    console.log('Auth state cleared');
  };

  if (isLoginComplete) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={48} sx={{ color: theme.palette.primary.main }} />
          <Typography variant="body2" color="text.secondary">
            Signing you in...
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.4,
          background: `
            radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary.main, 0.1)} 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, ${alpha(theme.palette.info.main, 0.05)} 0%, transparent 50%)
          `,
        }}
      />

      {/* Back to Landing Button */}
      <IconButton
        onClick={() => navigate('/')}
        sx={{
          position: 'absolute',
          top: { xs: 16, md: 24 },
          left: { xs: 16, md: 24 },
          color: theme.palette.text.secondary,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          zIndex: 10,
          '&:hover': {
            backgroundColor: 'white',
            color: theme.palette.primary.main,
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <ArrowBack />
      </IconButton>

      <Grid container sx={{ minHeight: '100vh' }}>
        {/* Left Side - Branding */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm">
            <Box sx={{ textAlign: 'center', px: 4 }}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: 4,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 4,
                  boxShadow: '0 20px 40px rgba(26, 54, 93, 0.3)',
                }}
              >
                <Business sx={{ fontSize: 60, color: 'white' }} />
              </Box>

              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                }}
              >
                Valuate
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  color: '#475569',
                  mb: 4,
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                AI-Powered Hotel Valuation Platform
              </Typography>

              <Stack spacing={3}>
                {[
                  { icon: TrendingUp, text: 'Advanced Market Analytics' },
                  { icon: Analytics, text: 'Real-time Property Insights' },
                  { icon: Security, text: 'Enterprise-grade Security' },
                ].map((feature, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <feature.icon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                    </Box>
                    <Typography variant="body1" sx={{ fontWeight: 500, color: '#475569' }}>
                      {feature.text}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Container>
        </Grid>

        {/* Right Side - Login Form */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
    <Container maxWidth="sm">
            <Fade in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  overflow: 'hidden',
                  mx: { xs: 2, sm: 4 },
                  my: { xs: 4, md: 0 },
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                  {/* Mobile Logo */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, textAlign: 'center', mb: 4 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        boxShadow: '0 8px 24px rgba(26, 54, 93, 0.3)',
                      }}
                    >
                      <Business sx={{ fontSize: 32, color: 'white' }} />
                    </Box>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      Valuate
                    </Typography>
                  </Box>

                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: '#1a365d',
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: { xs: '1.75rem', md: '2.25rem' },
                    }}
                  >
                    Welcome Back
        </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#64748b',
                      mb: 4,
                      textAlign: { xs: 'center', md: 'left' },
                      fontSize: '1.1rem',
                      fontWeight: 500,
                    }}
                  >
                    Sign in to access your hotel valuation dashboard
        </Typography>

                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        border: 'none',
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        '& .MuiAlert-message': {
                          fontWeight: 500,
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            color: '#1a365d',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                          }}
                        >
                          Email Address
                        </Typography>
                        <TextField
                          fullWidth
                          id="email"
                          name="email"
                          type="email"
                          required
                          autoComplete="email"
                          autoFocus
                          disabled={loading}
                          placeholder="Enter your email address"
                          InputProps={{
                            startAdornment: (
                              <Email sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.background.default, 0.8),
                              '& fieldset': {
                                border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: '2px',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                                boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                              },
                              transition: 'all 0.2s ease-in-out',
                            },
                            '& .MuiInputBase-input': {
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 500,
                              color: '#1a202c !important',
                              '&::placeholder': {
                                color: '#a0aec0 !important',
                                opacity: 1,
                              },
                              '&:focus': {
                                color: '#1a202c !important',
                              },
                            },
                            '& input': {
                              color: '#1a202c !important',
                              '&:focus': {
                                color: '#1a202c !important',
                              },
                            },
                          }}
                        />
                      </Box>

                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 1,
                            color: '#1a365d',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                          }}
                        >
                          Password
                        </Typography>
                        <TextField
                          fullWidth
                          id="password"
                          name="password"
                          type={showPassword ? 'text' : 'password'}
                          required
                          autoComplete="current-password"
                          disabled={loading}
                          placeholder="Enter your password"
                          InputProps={{
                            startAdornment: (
                              <Lock sx={{ color: 'action.active', mr: 1.5, ml: 0.5, fontSize: 20 }} />
                            ),
                            endAdornment: (
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                                sx={{
                                  mr: 0.5,
                                  color: theme.palette.text.secondary,
                                  '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                    color: theme.palette.primary.main,
                                  },
                                }}
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              backgroundColor: alpha(theme.palette.background.default, 0.8),
                              '& fieldset': {
                                border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                              },
                              '&:hover fieldset': {
                                borderColor: theme.palette.primary.main,
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: '2px',
                              },
                              '&.Mui-focused': {
                                backgroundColor: 'white',
                                boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
                              },
                              transition: 'all 0.2s ease-in-out',
                            },
                            '& .MuiInputBase-input': {
                              py: 1.5,
                              fontSize: '1rem',
                              fontWeight: 500,
                              color: '#1a202c !important',
                              '&::placeholder': {
                                color: '#a0aec0 !important',
                                opacity: 1,
                              },
                              '&:focus': {
                                color: '#1a202c !important',
                              },
                            },
                            '& input': {
                              color: '#1a202c !important',
                              '&:focus': {
                                color: '#1a202c !important',
                              },
                            },
                          }}
                        />
                      </Box>

                      <Box sx={{ pt: 1 }}>
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={loading}
                          sx={{
                            py: 1.5,
                            borderRadius: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            boxShadow: '0 8px 32px rgba(26, 54, 93, 0.3)',
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                              transform: 'translateY(-2px)',
                              boxShadow: '0 12px 40px rgba(26, 54, 93, 0.4)',
                            },
                            '&:active': {
                              transform: 'translateY(0px)',
                            },
                            '&:disabled': {
                              background: theme.palette.action.disabledBackground,
                              transform: 'none',
                              boxShadow: 'none',
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          }}
                        >
                          {loading ? (
                            <Stack direction="row" spacing={2} alignItems="center">
                              <CircularProgress size={20} color="inherit" />
                              <Typography>Signing In...</Typography>
                            </Stack>
                          ) : (
                            'Sign In'
                          )}
                        </Button>
                      </Box>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 4 }}>
                    <Typography variant="body2" sx={{ px: 2, fontWeight: 600, color: '#64748b' }}>
                      or
                    </Typography>
                  </Divider>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/signup')}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: alpha(theme.palette.primary.main, 0.5),
                      color: theme.palette.primary.main,
                      borderWidth: '2px',
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                        borderWidth: '2px',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 20px rgba(26, 54, 93, 0.15)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    Create New Account
                  </Button>

                  {/* Forgot Password Link */}
                  <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => {/* TODO: Implement forgot password */}}
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        textDecoration: 'none',
                        '&:hover': {
                          color: theme.palette.primary.main,
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      Forgot your password?
                    </Link>
                  </Box>

                  {/* Development Mode Info */}
                  {(process.env as any).NODE_ENV === 'development' && (
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 4,
                        p: 2,
                        backgroundColor: alpha(theme.palette.warning.main, 0.04),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 600 }}>
                        <strong>DEV MODE</strong> - API: {apiInfo.url}
                        {apiInfo.status && (
                          <Box
                            component="span"
                            sx={{
                              ml: 1,
                              px: 1,
                              py: 0.25,
                              borderRadius: 1,
                              bgcolor: apiInfo.status === 'connected' ? 'success.light' : 'warning.light',
                              color: apiInfo.status === 'connected' ? 'success.dark' : 'warning.dark',
                              fontSize: '0.65rem',
                            }}
                          >
                            {apiInfo.status}
                          </Box>
                        )}
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        onClick={clearAuthState}
                        sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                      >
                        Clear Auth State
                      </Button>
                    </Paper>
                  )}
                </CardContent>
              </Card>
            </Fade>
          </Container>
        </Grid>
      </Grid>
      </Box>
  );
};

export default LoginPage;
